import {AppUrls} from 'shared/config/model/urls';

import {MouseEvent, useCallback} from 'react';
import {generatePath, useLocation, useNavigate} from 'react-router-dom';
import {toast} from 'react-toastify';
import {t} from '@lingui/core/macro';

import {domainIncidentAPI} from 'features/domain/api/domainIncidentAPI';
import {getDomainIncidentExpireFormat} from 'features/domain/lib/domainMonitorView';
import useOpenState from 'shared/lib/hooks/useOpenState';
import {iriToId} from 'shared/api/iriHelper';
import {isTranslate} from 'shared/lib/lang';

import IDomainIncident from './DomainIncident/IDomainIncident';

export interface IUseDomainIncidentRow {
    isModalOpen: boolean,
    closeModal: () => void,
    expireFormat: string,
    onRowClick: (_event: MouseEvent<HTMLTableRowElement>) => void,
    onButtonClick: (_event: MouseEvent<HTMLTableCellElement>) => void,
    onIgnoreClick: (_event: MouseEvent) => Promise<void>,
}

export const useDomainIncidentRow = (item: IDomainIncident): IUseDomainIncidentRow => {
    const modal = useOpenState();
    const navigate = useNavigate();
    const {pathname} = useLocation();
    const [updateDomainIncident] = domainIncidentAPI.useUpdateDomainIncidentMutation();

    const isDetailsPage = pathname.includes('details');
    const domainMonitorId = iriToId(item.domain_monitor);
    const {open} = modal;

    const onRowClick = useCallback((event: MouseEvent<HTMLTableRowElement>) => {
        event.stopPropagation();
        open();
    }, [open]);

    const onButtonClick = useCallback((event: MouseEvent<HTMLTableCellElement>) => {
        event.stopPropagation();
        event.preventDefault();
        if (isDetailsPage) {
            open();
        } else if (domainMonitorId) {
            navigate(generatePath(AppUrls.DOMAIN_MONITOR_DETAILS, {domainMonitorId}));
        } else {
            toast.info(t`Нет связанного монитора домена для перехода`);
        }
    }, [navigate, domainMonitorId, isDetailsPage, open]);

    const onIgnoreClick = useCallback(async(event: MouseEvent) => {
        event.stopPropagation();
        const result = await updateDomainIncident({id: item.id, ignore: true});
        if (!('error' in result)) {
            toast.info(t`Инцидент домена проигнорирован`);
        }
    }, [item.id, updateDomainIncident]);

    return {
        isModalOpen: modal.isOpen,
        closeModal: modal.close,
        expireFormat: getDomainIncidentExpireFormat(isTranslate()),
        onRowClick,
        onButtonClick,
        onIgnoreClick,
    };
};

export default useDomainIncidentRow;
